import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, ArrowRight } from "lucide-react";

import { submitLead } from "@/lib/leads.functions";
import { SiteHeader } from "@/components/SiteHeader";
import { PageIntro } from "@/components/PageIntro";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { company } from "@/data/site";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Get Your Free Roofing & Solar Estimate – McKay Roofing & Solar" },
      { name: "description", content: "Request your free roofing or solar estimate from McKay Roofing & Solar. Fast response, honest guidance, and no-pressure quotes across California." },
      { name: "keywords", content: "free roofing estimate California, solar estimate Southern California, McKay Roofing & Solar contact, roofing quote, solar installation quote" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { property: "og:title", content: "Get Your Free Roofing & Solar Estimate" },
      { property: "og:description", content: "Free, no-pressure roofing and solar estimates across California." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const submit = useServerFn(submitLead);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;

    if (!smsConsent) {
      toast.error("Please agree to the SMS consent to continue.");
      return;
    }

    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name: String(fd.get("name") || ""),
      phone: String(fd.get("phone") || ""),
      email: String(fd.get("email") || ""),
      service: String(fd.get("postal") || ""),
      message: String(fd.get("message") || ""),
      source: "website-contact-form",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
    };

    if (!payload.name || !payload.email) {
      toast.error("Please fill in your name and email.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await submit({ data: payload });
      if (res?.ok) {
        form.reset();
        setSmsConsent(false);
        navigate({ to: "/thank-you" });
      } else {
        toast.error(res?.error || "Something went wrong. Please call us instead.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1 bg-primary">
        <section className="mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <form className="space-y-5 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8" onSubmit={handleSubmit}>
                <div className="relative pb-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-block h-8 w-1.5 rounded-full bg-primary" />
                    <h2 className="text-2xl font-extrabold text-primary sm:text-3xl">Get a Free Quote</h2>
                  </div>
                  <div className="mt-3 h-0.5 w-full rounded-full bg-gradient-to-r from-primary via-primary/60 to-transparent" />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input id="name" name="name" required placeholder="John Smith" className="mt-1.5 placeholder:text-foreground/70" />
                  </div>
                  <div>
                    <Label htmlFor="postal">Postal Code</Label>
                    <Input id="postal" name="postal" placeholder="ZIP or postal code" className="mt-1.5 placeholder:text-foreground/70" />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="email">Email <span className="text-primary">*</span></Label>
                    <Input id="email" name="email" type="email" required placeholder="your@email.com" className="mt-1.5 placeholder:text-foreground/70" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="(858) 541-1097" className="mt-1.5 placeholder:text-foreground/70" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Short Message About Your Needs</Label>
                  <Textarea id="message" name="message" rows={5} placeholder="Tell us about your roofing or solar project..." className="mt-1.5 placeholder:text-foreground/70" />
                </div>

                <p className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm font-semibold text-primary">
                  Your message goes straight to my phone. I'll get back to you as soon as I'm available.
                </p>

                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-muted/30 p-4 text-xs leading-relaxed text-foreground/85">
                  <Checkbox
                    checked={smsConsent}
                    onCheckedChange={(v) => setSmsConsent(v === true)}
                    className="mt-0.5 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                    aria-label="SMS consent"
                    required
                  />
                  <span>
                    I agree to receive text messages from McKay Roofing &amp; Solar regarding my quote request, appointments, and project updates. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out.
                  </span>
                </label>

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="group w-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/90 active:translate-y-0"
                >
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <>Get My Free Estimate <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>
                  )}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>
                  <span className="mx-2">|</span>
                  <a href="/terms" className="underline hover:text-primary">Terms &amp; Conditions</a>
                </p>
              </form>
            </div>

            <div className="space-y-6 lg:col-span-2">
              <div className="rounded-2xl border border-border bg-card p-6">
                <h2 className="text-xl font-extrabold text-primary">Reach us directly</h2>
                <ul className="mt-4 space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <Phone className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <div className="font-semibold">Call</div>
                      <a href={company.phoneHref} className="text-muted-foreground hover:text-primary">{company.phone}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <div className="font-semibold">Email</div>
                      <a href={`mailto:${company.email}`} className="break-all text-muted-foreground hover:text-primary">{company.email}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <div className="font-semibold">Southern California Office</div>
                      <div className="text-muted-foreground">9520 Padgett St, Suite 107,<br />San Diego, CA 92126</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <div className="font-semibold">Available Hours</div>
                      <div className="text-muted-foreground">Mon – Fri: 7:00 AM – 4:30 PM<br />Sat &amp; Sun: Closed</div>
                    </div>
                  </li>
                </ul>
              </div>
              <div
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
                onClick={(e) => {
                  const f = e.currentTarget.querySelector("iframe");
                  if (f) f.classList.add("is-active");
                }}
                onMouseLeave={(e) => {
                  const f = e.currentTarget.querySelector("iframe");
                  if (f) f.classList.remove("is-active");
                }}
              >
                <iframe
                  title="McKay Roofing and Solar location map"
                  src="https://maps.google.com/maps?q=McKay+Roofing+and+Solar,+9520+Padgett+St+STE+107,+San+Diego,+CA+92126&t=m&z=15&ie=UTF8&iwloc=&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-64 w-full border-0"
                />
                <a
                  href="https://www.google.com/maps/place/McKay+Roofing+and+Solar/@32.7286,-117.0169,15z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 border-t border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  Open in Google Maps
                </a>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-primary to-secondary p-6 text-primary-foreground">
                <div className="text-sm font-bold uppercase tracking-wider opacity-90">Need it fast?</div>
                <p className="mt-2 text-base">Same-day response during business hours. Prefer to talk now? Just call – we usually answer the phone.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
